// ===== 出荷一覧 =====

import { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { DataLink } from "../../../components/links/EntityLinks";
import { ContentLoading } from "../../../components/Loading";
import { EmptyState } from "../../../components/EmptyState";
import { FetchErrorAlert } from "../../../components/FetchErrorAlert";
import { StatusAccordion } from "../../../components/StatusAccordion";
import { DateGroupedAccordions, DateGroupSummary } from "../../../components/DateGroupedAccordions";
import { carryOutRow } from "../../../schema/console/carry_out";
import {
  ConstituentsById, MaterialsById, activeLots, attachedTotal, carryOutShippedAtEpoch, isCarryOutShipped,
  mainMaterialLabel, plannedTotal, shippedGroupKey,
} from "./helpers";
import { LotTable } from "./LotTable";

export function CarryOutListPage({ pageContext, materialsById, constituentsById }: { pageContext: PageContext; materialsById: MaterialsById; constituentsById: ConstituentsById }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const [rows, setRows] = useState<carryOutRow[] | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let active = true;
    setFetchError(false);
    api.carry_out.get()
      .then((data) => { if (active) { setRows(data); } })
      .catch(() => { if (active) { setRows([]); setFetchError(true); } });
    return () => { active = false; };
  }, [api]);

  if (rows === null) {
    return <ContentLoading />;
  }
  if (fetchError) {
    return <FetchErrorAlert />;
  }

  // 未出荷先頭 → 出荷日降順(mock CarryOutPage.tsx 準拠)。未出荷同士/出荷済同士は登録日降順。
  const sorted = [...rows].sort((a, b) => {
    const ka = carryOutShippedAtEpoch(a);
    const kb = carryOutShippedAtEpoch(b);
    if (ka === 0 && kb === 0) {
      return b.createdAt - a.createdAt;
    }
    if (ka === 0) {
      return -1;
    }
    if (kb === 0) {
      return 1;
    }
    return kb - ka;
  });

  return (
    <Stack p={3} gap={2} maxWidth={1400} mx="auto">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h4" fontWeight="bold">出荷一覧</Typography>
        <Button variant="contained" onClick={() => navigate("new/")}>新規出荷</Button>
      </Stack>

      <Alert severity="info" sx={{ py: 0.5 }}>出荷登録で「どの材料をどの LotNo で何缶」貼付するかが確定します。「出荷一覧」はフラット表示、「出荷日」は出荷日グルーピング表示。</Alert>
      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tab label="出荷一覧" />
          <Tab label="出荷日" />
        </Tabs>
        <Box>
          {tab === 0 && (
            <Stack p={1} gap={1}>
              {sorted.map((co) => <CarryOutAccordion key={co.id} co={co} materialsById={materialsById} constituentsById={constituentsById} />)}
              {sorted.length === 0 && <EmptyState message="出荷履歴なし" />}
            </Stack>
          )}
          {tab === 1 && <ShippedDateGroups rows={sorted} materialsById={materialsById} constituentsById={constituentsById} />}
        </Box>
      </Paper>
    </Stack>
  );
}

function ShippedDateGroups({ rows, materialsById, constituentsById }: { rows: carryOutRow[]; materialsById: MaterialsById; constituentsById: ConstituentsById }) {
  const summaryOf = (list: carryOutRow[]): DateGroupSummary => ({
    completedCount: list.filter(isCarryOutShipped).length,
    totalCount: list.length,
    plannedTotal: list.reduce((s, co) => s + plannedTotal(co), 0),
    attachedTotal: list.reduce((s, co) => s + attachedTotal(co), 0),
  });

  return (
    <DateGroupedAccordions
      rows={rows}
      groupKeyOf={shippedGroupKey}
      summaryOf={summaryOf}
      dateChipLabel="出荷日"
      countUnitLabel="出荷"
      attachedUnitLabel="貼付"
      renderRow={(co) => <CarryOutAccordion key={co.id} co={co} materialsById={materialsById} constituentsById={constituentsById} />}
      emptyState={<EmptyState message="出荷履歴なし" />}
    />
  );
}

function CarryOutAccordion({ co, materialsById, constituentsById }: { co: carryOutRow; materialsById: MaterialsById; constituentsById: ConstituentsById }) {
  const navigate = useNavigate();
  const lots = activeLots(co);
  const shipped = isCarryOutShipped(co);
  const shippedAt = carryOutShippedAtEpoch(co);
  const matLabel = mainMaterialLabel(co, materialsById);
  const header = (
    <>
      <DataLink type="carry-out" id={co.id} onClick={(e) => e.stopPropagation()} />
      {shipped && <Chip size="small" color="success" variant="outlined" label={shippedAt > 0 ? `出荷日 ${new Time(shippedAt).Format("YYYY/MM/dd")}` : "出荷済"} />}
      {!shipped && <Chip size="small" color="warning" variant="outlined" label={`未出荷  ${attachedTotal(co)}/${plannedTotal(co)}`} />}
      {matLabel !== "" && <Chip size="small" variant="outlined" label={matLabel} />}
      <Chip size="small" variant="outlined" label={`${lots.length}ロット / ${plannedTotal(co)}缶`} />
      <Box flex={1} />
      <Typography variant="caption" color="text.secondary">{co.User.name}</Typography>
    </>
  );
  return (
    <StatusAccordion header={header}>
      <Stack gap={1}>
        {!shipped && (
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Button variant="outlined" size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/console/carry-out/${co.id}/lot/new/`); }}>ロット追加</Button>
            <Typography variant="caption" color="text.secondary">{lots.length === 0 ? "まずは「ロット追加」で材料ロットを登録してください" : "QR読込は下の表の各ロット行から行ってください"}</Typography>
          </Stack>
        )}
        {lots.length === 0 && <Typography variant="caption" color="text.secondary">ロット未登録</Typography>}
        {lots.length > 0 && <LotTable co={co} materialsById={materialsById} constituentsById={constituentsById} />}
      </Stack>
    </StatusAccordion>
  );
}
